import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Crown, Loader2, Sparkles, Settings } from "lucide-react";

/** AI analytics plans — billed on Stripe, entirely separate from the gaming wallet. */
export default function PlansCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/billing/plans").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buy = async (plan) => {
    setBusy(plan);
    try {
      const { data: res } = await api.post("/billing/checkout", {
        plan, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { data: res } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing portal unavailable");
      setBusy("");
    }
  };

  if (!data) return null;
  const ent = data.entitlement || {};

  return (
    <div className="border border-[#7C3AED]/40 bg-[#7C3AED]/[0.06] p-6" data-testid="plans-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-[#C4B5FD]" />
        <div className="text-[#C4B5FD] text-xs uppercase tracking-widest">AI Analytics</div>
      </div>
      <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display text-3xl">PICK COACH</h2>
        <div className="text-right">
          <div className="text-white/40 text-xs uppercase tracking-widest">Status</div>
          <div className="font-display text-xl" data-testid="plans-status">
            {ent.sub_active ? (
              <span className="text-[#FFD700]">COACH PRO</span>
            ) : ent.pass_active ? (
              <span className="text-[#FFD700]">PASS ACTIVE</span>
            ) : (
              <span className="text-white/60">{ent.free_remaining ?? 0} FREE LEFT</span>
            )}
          </div>
        </div>
      </div>

      {ent.sub_active ? (
        <>
          <p className="text-white/60 text-sm mb-3">
            Unlimited AI reads on every slate. Renews monthly — cancel any time in the billing portal.
          </p>
          <button
            onClick={manage}
            disabled={busy === "portal"}
            data-testid="plans-manage-btn"
            className="w-full py-3 border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest text-sm rounded-sm inline-flex items-center justify-center gap-2"
          >
            {busy === "portal" ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />}
            Manage subscription
          </button>
        </>
      ) : (
        <>
          <p className="text-white/60 text-sm mb-4">
            {ent.free_limit} free reads a week, then unlock unlimited AI reads. Charged on your card —
            your gaming wallet is never touched.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <button
              onClick={() => buy("pass")}
              disabled={!!busy}
              data-testid="plans-buy-pass-btn"
              className="py-3 px-3 bg-[#FFD700] hover:bg-[#e6c200] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-xs rounded-sm inline-flex items-center justify-center gap-2"
            >
              {busy === "pass" ? <Loader2 size={13} className="animate-spin" /> : <Crown size={13} />}
              {ent.pass_active ? "Extend" : `${ent.pass_days}-day pass`} · ${Number(ent.pass_price || 0).toFixed(2)}
            </button>
            <button
              onClick={() => buy("monthly")}
              disabled={!!busy}
              data-testid="plans-buy-sub-btn"
              className="py-3 px-3 bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs rounded-sm inline-flex items-center justify-center gap-2"
            >
              {busy === "monthly" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Monthly · ${Number(ent.sub_price || 0).toFixed(2)}/mo
            </button>
          </div>
          {ent.pass_active && (
            <div className="mt-2 text-white/40 text-[10px] uppercase tracking-widest">
              Pass runs through {new Date(ent.pass_expires_at).toLocaleDateString()}
            </div>
          )}
        </>
      )}
      <div className="mt-3 text-white/30 text-[10px] leading-snug">
        Entertainment only. AI reads are suggestions, never guarantees — you always place your own picks.
      </div>
    </div>
  );
}
